//Custom components

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
//Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
import { accountSelector } from '@/store';
//Assets and miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import Card from '../../UI/Card/index.tsx';
import BarChart from '../../UI/Charts/BarChart';
import { CurrencyFormatDisplay } from '../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { Widget } from '../constants';
import WidgetLayout from '../WidgetLayout';

interface ProjectBudgetSummaryWidgetProps {
  dashboard: DashboardType;
}

interface BudgetDashboardDataInterface {
  name?: string;
  amount?: number | string;
}

interface BudgetSummaryDashboardDataInterface {
  id: number;
  name: string;
  budget?: BudgetDashboardDataInterface[] | null;
}

const normalizeBudgetItems = (budget: BudgetSummaryDashboardDataInterface['budget']): BudgetDashboardDataInterface[] => {
  if (!Array.isArray(budget)) {
    return [];
  }

  return budget;
};

const sumBudgetAmount = (budget: BudgetSummaryDashboardDataInterface['budget']) =>
  normalizeBudgetItems(budget).reduce((acc, item) => acc + (Number(item.amount) || 0), 0);

const ProjectBudgetSummaryWidget = ({ dashboard }: ProjectBudgetSummaryWidgetProps) => {
  const { data: currentClientAccount } = useSelector(accountSelector);
  const fetchProjectBudgetSummaryWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECT_BUDGET_SUMMARY,
  });

  const projectBudgetSummaryWidgetData = fetchProjectBudgetSummaryWidgetData?.data?.data;

  const projects = useMemo(() => {
    if (!Array.isArray(projectBudgetSummaryWidgetData)) {
      return [] as BudgetSummaryDashboardDataInterface[];
    }
    return projectBudgetSummaryWidgetData as BudgetSummaryDashboardDataInterface[];
  }, [projectBudgetSummaryWidgetData]);

  const chartData = useMemo(() => {
    return projects.map((project) => ({
      name: project.name?.trim() ? project.name : `Project ${project.id}`,
      total: sumBudgetAmount(project.budget),
    }));
  }, [projects]);

  const projectsName = useMemo(() => chartData.map((project) => project.name), [chartData]);
  const projectSources = useMemo(() => chartData.map((project) => project.total), [chartData]);

  const totalDevelopmentValue = projectSources.reduce((acc, next) => acc + next, 0);

  const cclfApprovedLoans = useMemo(() => {
    return projects.map((project) =>
      normalizeBudgetItems(project.budget).reduce((acc, item) => {
        if (item.name?.toLowerCase() === 'cclf loan') {
          return acc + (Number(item.amount) || 0);
        }
        return acc;
      }, 0)
    );
  }, [projects]);

  const totalCCLFApprovedLoans = cclfApprovedLoans.reduce((acc, next) => acc + next, 0);

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECT_BUDGET_SUMMARY}>
      <section className='flex gap-8 h-full'>
        <Card title='Project Budget Summary' error={fetchProjectBudgetSummaryWidgetData.isError}>
          {Array.isArray(projectBudgetSummaryWidgetData) && (
            <>
              <div className='flex w-full flex-row justify-between items-center px-8 py-4'>
                <span className='flex flex-col'>
                  <p className='text-gray-400 text-sm'>Total Development Value</p>
                  <p className='text-black-800 flex font-bold text-lg'>
                    <CurrencyFormatDisplay value={totalDevelopmentValue} prefix='$' />
                  </p>
                </span>

                {currentClientAccount.subdomain === 'chicagorebuild' && (
                  <span className='flex flex-col'>
                    <p className='text-gray-400 text-sm'>Rebuild Loans Approved</p>
                    <p className='text-tertiaryColor-normal font-bold text-lg'>
                      <CurrencyFormatDisplay value={totalCCLFApprovedLoans} prefix='$' />
                    </p>
                  </span>
                )}
              </div>
              <div className='w-full px-6 pb-4'>
                <BarChart
                  dataLabels={projectsName}
                  colors={false}
                  datasets={projectSources}
                  xAxisLabel='Project'
                  yAxisLabel='Development Value'
                  formatValuesAsCurrency
                />
              </div>
            </>
          )}
        </Card>
      </section>
    </WidgetLayout>
  );
};

export default ProjectBudgetSummaryWidget;
